import React from 'react';
import { isObject, isArray } from '../helpers';
import {
  isAttachment,
  isTranslation,
  isAccreditation,
  isExtra,
  isTheme,
  isSegment,
  isProgram,
} from '../typeCheckers';
import { getResolvedAttachment } from '../labelGetters';
import AttachmentDisplay from './AttachmentDisplay';
import TranslationDisplay from './TranslationDisplay';
import AccreditationDisplay from './AccreditationDisplay';
import ExtraDisplay from './ExtraDisplay';
import ThemeDisplay from './ThemeDisplay';
import SegmentDisplay from './SegmentDisplay';
import { ProgramDisplay } from './ProgramDisplays';
import ValueRenderer from './ValueRenderer';
import FieldLabel from './FieldLabel';

export default function ObjectValueDisplay({
  value,
  depth = 0,
  isNew = false,
  attachmentLabels,
  labels,
}) {
  if (!isObject(value)) {
    return <ValueRenderer value={value} />;
  }

  // Check for special object types first
  if (isAttachment(value)) {
    const resolvedAttachment = getResolvedAttachment(value, attachmentLabels);
    return (
      <AttachmentDisplay
        attachment={value}
        isNew={isNew}
        resolvedAttachment={resolvedAttachment}
      />
    );
  }
  if (isTranslation(value)) {
    return <TranslationDisplay translation={value} isNew={isNew} />;
  }
  if (isAccreditation(value, labels)) {
    return (
      <AccreditationDisplay
        accreditation={value}
        labels={labels}
        isNew={isNew}
      />
    );
  }
  if (isExtra(value, labels)) {
    return <ExtraDisplay extra={value} labels={labels} isNew={isNew} />;
  }
  if (isTheme(value, labels)) {
    return <ThemeDisplay theme={value} labels={labels} isNew={isNew} />;
  }
  if (isSegment(value, labels)) {
    return <SegmentDisplay segment={value} labels={labels} isNew={isNew} />;
  }
  if (isProgram(value)) {
    return (
      <ProgramDisplay
        program={value}
        labels={labels}
        isNew={isNew}
        attachmentLabels={attachmentLabels}
      />
    );
  }

  if (depth > 2) {
    return <span className="text-gray-500">[object]</span>;
  }

  const borderClass = isNew ? 'border-green-300' : 'border-gray-200';
  const keys = Object.keys(value);
  return (
    <div className={`space-y-1 pl-2 border-l-2 ${borderClass}`}>
      {keys.map((key) => (
        <div key={key}>
          <FieldLabel name={key} />
          {isObject(value[key]) ? (
            <ObjectValueDisplay
              value={value[key]}
              depth={depth + 1}
              attachmentLabels={attachmentLabels}
              labels={labels}
            />
          ) : isArray(value[key]) ? (
            <span className="text-xs text-gray-500">
              [{value[key].length} items]
            </span>
          ) : (
            <ValueRenderer value={value[key]} />
          )}
        </div>
      ))}
    </div>
  );
}
